import { localStorage } from 'dibs-browser-storage';
import { fetchQuery, graphql, Environment } from 'react-relay';

import { trackEvent } from 'dibs-tracking';

import {
    STORAGE_KEY_SOURCING_PDP_VISITS,
    SOURCING_MIN_PRICE,
    MEDIAN_ITEM_VALUE_INDEX,
} from './pdpSharedConsumerSourcingHelpersConstants';
import {
    evaluatePdpVisit,
    removeUserPdpVisitsInfo,
} from './pdpSharedConsumerSourcingEvaluationHelpers';
import { getUserPdpVisitsInfo } from './pdpSharedConsumerSourcingPdpInfoHelper';

import { pdpSharedConsumerSourcingTooltipDisplayHelpersQuery as QueryType } from './__generated__/pdpSharedConsumerSourcingTooltipDisplayHelpersQuery.graphql';

const query = graphql`
    query pdpSharedConsumerSourcingTooltipDisplayHelpersQuery($userId: String!, $itemId: String!) {
        viewer {
            disableConsumerSourcingModal: isEnabled(key: "disableConsumerSourcingModal")
            user(userId: $userId) {
                serviceId
                isVerifiedTrade
                isVip
            }
            item(itemId: $itemId) {
                serviceId
                pricing {
                    retailPrice {
                        convertedAmounts {
                            USD
                        }
                    }
                }
            }
        }
    }
`;

export const shouldSeeConsumerSourcingTooltip = async ({
    itemId,
    userId,
    environment,
}: {
    itemId: string;
    userId: string;
    environment: Environment;
}): Promise<boolean> => {
    try {
        const uniqueItemViewReached = evaluatePdpVisit({ userId });

        if (!uniqueItemViewReached) {
            return false;
        }

        const response = await fetchQuery<QueryType>(environment, query, {
            userId,
            itemId,
        });

        const viewer = response?.viewer;
        const user = viewer?.user;
        const disableConsumerSourcingModal = viewer?.disableConsumerSourcingModal;
        const isVerifiedTrade = user?.isVerifiedTrade;
        const isVip = user?.isVip;
        const item = viewer?.item;
        const itemPrice = item?.pricing?.retailPrice?.convertedAmounts?.USD;

        if (
            disableConsumerSourcingModal ||
            !itemPrice ||
            itemPrice < SOURCING_MIN_PRICE ||
            isVerifiedTrade ||
            isVip
        ) {
            return false;
        }

        return true;
    } catch (e) {
        // @ts-ignore
        if (window.newrelic) {
            // @ts-ignore
            window.newerelic.noticeError(`error fetching item price: ${e}`);
        }
    }

    return false;
};

export const suppressMessage: ({ userId }: { userId: string }) => void = ({ userId }) => {
    const updatedPdpUserSpecificVisits = removeUserPdpVisitsInfo({ userId });
    updatedPdpUserSpecificVisits.push({
        userId: userId,
        pdpInfo: [],
        suppressMessage: true,
    });
    localStorage.setItem(STORAGE_KEY_SOURCING_PDP_VISITS, updatedPdpUserSpecificVisits);
};

export const shouldSuppressMessage: ({ userId }: { userId: string }) => boolean = ({ userId }) => {
    const { userPdpVisitsInfo, userIndex } = getUserPdpVisitsInfo({ userId });
    const userPdp = userPdpVisitsInfo.splice(userIndex, 1)[0];
    return userIndex === -1 ? false : !!userPdp?.suppressMessage;
};

export const getSubmissionInfo: ({
    userId,
}: {
    userId: string;
}) => { retailPrice: number; medianPricePoint: number } = ({ userId }) => {
    const { userPdpVisitsInfo, userIndex } = getUserPdpVisitsInfo({ userId });
    const userPdp = userPdpVisitsInfo[userIndex];
    const mostRecentPdp = userPdp.pdpInfo[userPdp.pdpInfo.length - 1];
    const sortedVisits = userPdp.pdpInfo.sort((a, b) => a.itemPrice - b.itemPrice);

    return {
        retailPrice: mostRecentPdp?.itemPrice,
        medianPricePoint: sortedVisits?.[MEDIAN_ITEM_VALUE_INDEX]?.itemPrice,
    };
};

export const trackConsumerSourcingTooltip = ({ action }: { action: string }): void => {
    trackEvent({
        category: 'sales tools',
        label: 'consumer sourcing modal',
        action,
    });
};
